import { VscRunAll } from "react-icons/vsc";
import { TbChartGridDots } from "react-icons/tb";
import { IoAddCircleOutline } from "react-icons/io5";
import { RiDragMove2Line } from "react-icons/ri";
import { CgTapDouble } from "react-icons/cg";
import { CiTrash } from "react-icons/ci";
import { CiEraser } from "react-icons/ci";
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useWindowSelection } from "./useObjectSelection";
import {
  clearSelections,
  isSelectionModeActive,
  setDrawMode,
  setSelectionTool,
  getSelectionTool,
  toggleSelectionMode,
  selectionTools,
} from "../../redux/objectSelection";
import {
  isResourceInState,
  applyActionToSensorAnalysisState,
  setPreRayConfigurations,
} from "../../redux/sensorAnalysis";
import "./WindowSelectionBox.css";
import { colors } from "../../utils/colors";
import { Button } from "react-bootstrap";
import { BsTrash3 } from "react-icons/bs";
import { VscTriangleRight } from "react-icons/vsc";
import { useMeshSensorsCRUD } from "../CustomHooks";
import { getHasActiveFilters } from "../CustomHooks";
import { resources, actions } from "../../redux/sensorAnalysisConstants";
import { FaMesh } from "react-icons/fa";

const WindowSelectionBox = () => {
  const dispatch = useDispatch();

  const allWindows = useSelector((state) => state.windowData.allWindows);
  const filteredWindows = useSelector(
    (state) => state.windowData.filteredWindows
  );
  const windowFilters = useSelector((state) => state.windowData.filters);
  const resultArray = useSelector((state) => state.windowData.resultArray);
  const isDataLoaded = useSelector((state) => state.windowData.isLoaded);

  const selectedFloor = useSelector(
    (state) => state.floorVisibility.selectedFloors
  );

  const hasActiveFilters = getHasActiveFilters(windowFilters);

  const visibleWindows = hasActiveFilters
    ? filteredWindows
    : allWindows.filter((window) => {
        for (const floorIndex in resultArray) {
          const floor = resultArray[floorIndex];
          if (selectedFloor[floorIndex]) {
            for (const windowIndex in floor) {
              const floorWindow = floor[windowIndex];
              if (window.id === floorWindow.id) {
                return true;
              }
            }
          }
        }
        return false;
      });

  const { selectionCount, selectedWindowsData } = useWindowSelection();

  const isSelectionActive = useSelector((state) =>
    isSelectionModeActive(state)
  );
  const selectionTool = useSelector((state) => getSelectionTool(state));

  const handleToolChange = (tool) => {
    dispatch(setSelectionTool(tool));

    if (!isSelectionActive) {
      dispatch(toggleSelectionMode());
    }

    if (tool === selectionTools.DRAW || tool === selectionTools.ERASE) {
      dispatch(setDrawMode(true));
    } else {
      dispatch(setDrawMode(false));
    }
  };

  const handleClearSelections = () => {
    dispatch(clearSelections());
  };

  const handleClose = () => {
    dispatch(setDrawMode(false));
    dispatch(setSelectionTool(selectionTools.SINGLE));
    handleClearSelections();
    dispatch(applyActionToSensorAnalysisState(actions.ON_CLOSE));
    dispatch(setPreRayConfigurations([]));
  };

  const sensorsData = useSelector((state) => state.sensorAnalysis.sensorsData);

  const { remove } = useMeshSensorsCRUD();

  const handleRemoveMesh = async () => {
    const selectedWindowsSensors = sensorsData
      .filter((sensor) =>
        selectedWindowsData?.some((window) => window.id === sensor.surfaceId)
      )
      ?.map((sensor) => sensor.id);
    if (selectedWindowsSensors.length > 0) {
      await remove(selectedWindowsSensors);
      handleClearSelections();
    }
  };

  const sensoranalysisState = useSelector(
    (state) => state.sensorAnalysis.sensorAnalysisState
  );
  const visible = isResourceInState(
    sensoranalysisState,
    resources.WINDOW_SELECTION_BOX
  );
  if (!visible) return null;

  if (!isDataLoaded || allWindows.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        top: "250px",
        left: "20px",
        zIndex: 1000,
        pointerEvents: "auto",
      }}
    >
      <div className="compact-selection-panel">
        <div className="panel-header">
          <div className="panel-title">
            <span className="selection-icon">🔧</span>
            <span>
              Visible Windows ({visibleWindows.length}) - {selectionCount}{" "}
              Selected
            </span>
          </div>
          <button className="close-btn" onClick={handleClose} title="Close">
            ✕
          </button>
        </div>

        <div className="panel-content">
          <div className="selection-tools-horizontal">
            <button
              className={`tool-btn-compact ${
                selectionTool === selectionTools.SINGLE ? "active" : ""
              }`}
              onClick={() => handleToolChange(selectionTools.SINGLE)}
              title="Click individual windows to select"
            >
              <CgTapDouble color={colors.gray500()} />
            </button>
            <button
              className={`tool-btn-compact ${
                selectionTool === selectionTools.DRAW ? "active" : ""
              }`}
              onClick={() => handleToolChange(selectionTools.DRAW)}
              title="Draw rectangle to select windows"
            >
              <div
                style={{
                  padding: 1,
                  border: "1px dashed #64748b",
                  borderRadius: 3,
                  width: 16,
                  height: 16,
                }}
              >
                <div
                  style={{
                    backgroundColor: "#ff000074",
                    width: "100%",
                    height: "100%",
                  }}
                ></div>
              </div>
            </button>
            <button
              className={`tool-btn-compact ${
                selectionTool === selectionTools.ERASE ? "active" : ""
              }`}
              onClick={() => handleToolChange(selectionTools.ERASE)}
              title="Draw rectangle to deselect windows"
            >
              <CiEraser color="#64748b" />
            </button>
            <button
              className="tool-btn-compact "
              onClick={handleClearSelections}
              disabled={selectionCount === 0}
              title="Clear all selections"
            >
              <CiTrash color="#f00" />
            </button>
          </div>

          {selectionCount > 0 &&
            isResourceInState(
              sensoranalysisState,
              resources.ADD_MOVE_DELETE_MESHING_OPTIONS
            ) && (
              <>
                <h3 className="action-title">
                  <TbChartGridDots />
                  <span>Mesh Options</span>
                </h3>
                <div className="actions">
                  <Button
                    className="button-with-icon"
                    variant="danger"
                    onClick={handleRemoveMesh}
                  >
                    <BsTrash3 /> Remove
                  </Button>
                  <Button
                    variant="primary"
                    className="button-with-icon"
                    onClick={() => {
                      dispatch(
                        applyActionToSensorAnalysisState(actions.ON_ADD_MESH)
                      );
                    }}
                  >
                    <IoAddCircleOutline />
                    Add
                  </Button>
                  <Button
                    variant="primary"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                    onClick={() => {
                      dispatch(
                        applyActionToSensorAnalysisState(actions.ON_MOVE_MESH)
                      );
                    }}
                  >
                    <RiDragMove2Line />
                    Move
                  </Button>
                </div>

                <h3 className="action-title">
                  <VscRunAll />
                  <span>Run Options</span>
                </h3>
                <div className="actions">
                  <Button
                    className="button-with-icon"
                    variant="primary"
                    onClick={() => {
                      dispatch(
                        applyActionToSensorAnalysisState(actions.ON_RUN_PRE_RAY)
                      );
                    }}
                  >
                    <VscTriangleRight /> Pre Ray Tracing
                  </Button>
                  <Button
                    className="button-with-icon"
                    variant="primary"
                    onClick={() => {
                      dispatch(
                        applyActionToSensorAnalysisState(actions.ON_RUN_RAY)
                      );
                    }}
                  >
                    <VscTriangleRight /> Ray Tracing
                  </Button>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default WindowSelectionBox;
